// ExpertList.tsx - Expert Listeleme Sayfasi
// App Layout (Sidebar + Topbar + Footer)

import { Filter, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ExpertCard from '../components/expert/ExpertCard'
import Topbar from '../components/layout/Topbar'
import { Expert } from '../types/expert'

export default function ExpertList() {
  const navigate = useNavigate()
  const [experts, setExperts] = useState<Expert[]>([])
  const [filteredExperts, setFilteredExperts] = useState<Expert[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [loading, setLoading] = useState(true)

  // Fetch experts
  useEffect(() => {
    void fetchExperts()
  }, [])

  const fetchExperts = async () => {
    try {
      const response = await fetch('http://localhost:8004/api/v1/experts/')
      const data = (await response.json()) as Expert[]
      setExperts(data)
      setFilteredExperts(data)
    } catch (error) {
      console.error('Expert fetch error:', error)
    } finally {
      setLoading(false)
    }
  }

  // Search + Filter
  useEffect(() => {
    let filtered = experts

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(e => e.category_id === selectedCategory)
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(query) ||
        e.category_id.toLowerCase().includes(query) ||
        e.nationality?.toLowerCase().includes(query)
      )
    }

    if (filtered) {
      setFilteredExperts(filtered)
    }
  }, [searchQuery, selectedCategory, experts])

  // Kategori bazli gruplama
  const groupedExperts = filteredExperts.reduce((acc, expert) => {
    const cat = expert.category_id
    if (!acc[cat]) {
      acc[cat] = []
    }
    acc[cat].push(expert)
    return acc
  }, {} as Record<string, Expert[]>)

  const categories = Array.from(new Set(experts.map(e => e.category_id)))

  return (
    <div className="flex h-screen" style={{ background: 'var(--body-bg)' }}>      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar />

        <main className="flex-1 overflow-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Uzmanlar
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              {filteredExperts.length} uzman bulundu
            </p>
          </div>

          {/* Search + Filter */}
          <div className="card mb-6" style={{ background: 'var(--card-bg)' }}>
            <div className="card-body p-4">
              <div className="flex gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--text-secondary)' }}
                  />
                  <input
                    type="text"
                    placeholder="Uzman ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded"
                    style={{
                      background: 'var(--input-bg)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                {/* Category Filter */}
                <div className="flex items-center gap-2">
                  <Filter size={20} style={{ color: 'var(--text-secondary)' }} />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 rounded"
                    style={{
                      background: 'var(--input-bg)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="all">Tum Kategoriler</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Expert Grid (Kategori bazli) */}
          {loading ? (
            <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
              Yukleniyor...
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedExperts).map(([category, categoryExperts]) => (
                <div key={category}>
                  <h2
                    className="text-xl font-semibold mb-4"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {category} ({categoryExperts.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryExperts.map(expert => (
                      <ExpertCard
                        key={expert.id}
                        expert={expert}
                        onClick={() => navigate(`/experts/${expert.id}`)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
